/**
 * The live run view (tasks 13.11, 13.12, 13.13, 13.14).
 *
 * Three things at once: the streamed log, the live browser when Watch was on, and
 * a Stop control.
 *
 * TWO DETAILS THAT MATTER MORE THAN THEY LOOK
 *
 * 1. Auto-scroll follows the tail ONLY while the operator is already at the bottom.
 *    Yanking the view back down while someone is reading a failure further up is
 *    the single most irritating thing a log viewer can do.
 *
 * 2. Closing this view NEVER stops the run. The stream is a side channel; the run
 *    belongs to the server. Task 13.13 requires results to be identical whether the
 *    live view is open or closed, so nothing here may influence execution.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Filmstrip } from './Filmstrip';
import { RunReports } from './RunReports';
import { TestDiagnostics } from './TestDiagnostics';
import { api, streamLogs, type LogLine, type RunSummary } from '../api';
import { LiveResults, appOf } from './LiveResults';

interface Outcome {
  title: string;
  file: string;
  suite?: string;
  status: string;
  durationMs: number;
  unstable?: boolean;
  error?: string;
  newlyFailing?: boolean;
}

const STATUS_PILL: Record<string, string> = {
  queued: 'pill-info',
  running: 'pill-accent',
  passed: 'pill-pass',
  failed: 'pill-fail',
  stopped: 'pill-info',
  error: 'pill-fail',
};

interface Props {
  runId: string;
  onClose: () => void;
}

/**
 * Colour a log line by WHAT IT SAYS, not which stream it came from.
 *
 * Jest, pytest and npm all write progress and warnings to stderr, so colouring by
 * stream painted an entire green Unit run bright red — "PASS src/…" in the colour
 * this interface reserves for failure. Someone glancing at the screen concluded the
 * build was broken when every test had passed.
 *
 * Content is the honest signal: a line announcing a pass is green whatever pipe it
 * arrived on, a line announcing a failure is red, warnings are amber, and anything
 * unclassified stays neutral rather than being guessed at.
 */
const PASS_LINE = /^\s*(PASS\b|✓|ok\s|\d+ passed)|PASSED\b/;
const FAIL_LINE = /^\s*(FAIL\b|✘|×|✕)|\b(FAILED|ERROR|Error:|error:)\b|\d+ failed/;
const WARN_LINE = /^\s*(npm warn|warning:|WARN\b)|\bdeprecated\b/i;

/** "4m 12s" — short enough for a header, precise enough to be useful. */
function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function isOlderThanADay(iso: string): boolean {
  return Date.now() - new Date(iso).getTime() > 24 * 3600 * 1000;
}

function lineColour(line: LogLine): string {
  if (line.stream === 'event') return 'hsl(var(--accent))';
  if (FAIL_LINE.test(line.text)) return 'hsl(var(--fail))';
  if (PASS_LINE.test(line.text)) return 'hsl(var(--pass))';
  if (WARN_LINE.test(line.text)) return 'hsl(var(--warn))';
  return 'hsl(var(--text-muted))';
}

export function RunLive({ runId, onClose }: Props) {
  const [run, setRun] = useState<RunSummary | null>(null);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [lines, setLines] = useState<LogLine[]>([]);
  const [following, setFollowing] = useState(true);
  const [stopping, setStopping] = useState(false);
  const [shots, setShots] = useState<Array<{ index: number; label: string }>>([]);
  const [expandedOutcome, setExpandedOutcome] = useState<string | null>(null);

  const logRef = useRef<HTMLPreElement>(null);

  /**
   * The live view is only mounted once its endpoint actually answers.
   *
   * The runner container needs a few seconds to exist — and on a host that needs
   * the relaxed retry, the first container dies and a second one starts. Mounting
   * the iframe during that window makes nginx return 502, the browser renders its
   * error page, and NOTHING ever reloads it: the URL does not change again, so the
   * panel stays showing "502 Bad Gateway" for the rest of the run while a healthy
   * browser streams behind it. That was the whole bug — every layer underneath was
   * working.
   *
   * Probing with fetch rather than reloading the iframe on a timer, because a
   * reload would tear down a live VNC session every few seconds once connected.
   */
  const [vncReady, setVncReady] = useState(false);

  // Ticks once a second, only while the run is live, so the elapsed time in the
  // header is real rather than the value from whenever the page last re-rendered.
  const [now, setNow] = useState(() => Date.now());

  /**
   * The last frame that had something on it, held over the gaps.
   *
   * Playwright gives every test its own browser context, so the window is created
   * and destroyed per test and the display is genuinely empty in between — the VNC
   * stream is correct, it is showing an empty X root. Flashing to black between
   * every test reads as breakage, so the last non-empty frame is frozen over it
   * with a label saying what is happening.
   */
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [heldFrame, setHeldFrame] = useState<string | null>(null);
  const [betweenTests, setBetweenTests] = useState(false);

  // Poll the run record for status; the log itself arrives by stream.
  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      try {
        const { run: latest } = await api.run(runId);
        if (!cancelled) {
          setRun(latest);
          setOutcomes(((latest as { outcomes?: Outcome[] }).outcomes ?? []) as Outcome[]);
        }
        // Stop polling once the run reaches a terminal state.
        if (!cancelled && ['passed', 'failed', 'stopped', 'error'].includes(latest.status)) return;
      } catch {
        /* transient — the next tick retries */
      }
      if (!cancelled) setTimeout(tick, 2000);
    };

    tick();
    return () => {
      cancelled = true;
    };
  }, [runId]);

  useEffect(() => {
    setVncReady(false);
    const url = run?.vncUrl;
    if (!url) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const probe = async () => {
      if (cancelled) return;
      try {
        const res = await fetch(url, { method: 'GET', cache: 'no-store' });
        if (!cancelled && res.ok) return setVncReady(true);
      } catch {
        /* not up yet */
      }
      if (!cancelled) timer = setTimeout(probe, 1500);
    };

    probe();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [run?.vncUrl]);

  useEffect(() => {
    if (!vncReady) { setHeldFrame(null); setBetweenTests(false); return; }

    let cancelled = false;
    // Small enough that sampling costs nothing; large enough to recognise the page.
    const scratch = document.createElement('canvas');
    scratch.width = 480;
    scratch.height = 300;
    const ctx = scratch.getContext('2d', { willReadFrequently: true });

    const sample = () => {
      if (cancelled || !ctx) return;
      try {
        // Same-origin (the console serves /vnc/ from its own host), so the frame's
        // canvas is readable. It carries no cross-origin image data, so it is not
        // tainted and can be exported.
        const canvas = frameRef.current?.contentDocument?.querySelector<HTMLCanvasElement>(
          '#noVNC_container canvas',
        );
        if (canvas && canvas.width > 0) {
          ctx.drawImage(canvas, 0, 0, scratch.width, scratch.height);
          const { data } = ctx.getImageData(0, 0, scratch.width, scratch.height);

          // "Empty" means UNIFORM, not dark.
          //
          // The first version counted lit pixels, so a black X root counted as empty
          // — but a browser sitting on about:blank is a full white rectangle, which
          // counted as content and became the frame held over the gap. The operator
          // was left staring at a white screen, which tells them nothing. What both
          // states share is that nothing is drawn: one colour, no variation. Sample
          // the spread instead.
          let min = 255;
          let max = 0;
          let sum = 0;
          let samples = 0;
          for (let i = 0; i < data.length; i += 160) {
            const luma = (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000;
            if (luma < min) min = luma;
            if (luma > max) max = luma;
            sum += luma;
            samples++;
          }
          const mean = sum / Math.max(samples, 1);
          let variance = 0;
          for (let i = 0; i < data.length; i += 160) {
            const luma = (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000;
            variance += (luma - mean) ** 2;
          }
          const spread = Math.sqrt(variance / Math.max(samples, 1));

          // A rendered page always has text and chrome, so its spread is large. A
          // blank display — black root or white about:blank — has almost none.
          const empty = spread < 6 || max - min < 16;
          setBetweenTests(empty);
          if (!empty) setHeldFrame(scratch.toDataURL('image/jpeg', 0.7));
        }
      } catch {
        /* the frame is mid-navigation; the next tick retries */
      }
    };

    const timer = setInterval(sample, 700);
    return () => { cancelled = true; clearInterval(timer); };
  }, [vncReady]);

  useEffect(() => {
    if (!(run?.status === 'running' || run?.status === 'queued')) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [run?.status]);

  // The log stream. EventSource handles reconnection and replays what was missed.
  useEffect(() => {
    setLines([]);
    const close = streamLogs(runId, (line) => {
      setLines((current) => {
        // Resume can re-deliver a line that is already held; keep it monotonic.
        if (current.length && line.seq <= current[current.length - 1].seq) return current;
        return [...current, line];
      });
    });
    return close;
  }, [runId]);

  // Follow the tail only while the operator has not scrolled away.
  useEffect(() => {
    if (following && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [lines, following]);

  const onScroll = useCallback(() => {
    const el = logRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setFollowing(atBottom);
  }, []);

  async function stop() {
    setStopping(true);
    try {
      const { run: stopped } = await api.stop(runId);
      setRun(stopped);
    } finally {
      setStopping(false);
    }
  }

  const active = run?.status === 'running' || run?.status === 'queued';

  // Same source Filmstrip reads from — screenshots only exist once the container
  // has exited and `archive()` has copied them out, so one more read after the run
  // finishes is what actually picks them up (task 13.17: "make sure we get the
  // reports we need" means the outcomes list should link to a real, already-collected
  // screenshot rather than a field the backend never filled in).
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.screenshots(runId)
        .then((r) => { if (!cancelled) setShots(r.screenshots); })
        .catch(() => { /* not available yet */ });
    };
    load();
    const timer = active ? setInterval(load, 4000) : undefined;
    if (!active) setTimeout(load, 1500);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [runId, active]);

  /** A screenshot's label is Playwright's own output-dir name for the test — the
   *  best link back to an outcome row without the backend threading a key through. */
  function shotsFor(o: Outcome): Array<{ index: number; label: string }> {
    const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '');
    const wanted = slug(o.title);
    return shots.filter((s) => slug(s.label).includes(wanted));
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="surface p-4">
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`pill ${
              active ? 'pill-accent'
              : run?.status === 'passed' ? 'pill-pass'
              : run?.status === 'failed' || run?.status === 'error' ? 'pill-fail'
              : 'pill-info'
            }`}
            data-testid="run-status"
          >
            {active && <span className="dot dot-live" />}
            {run?.status ?? 'loading'}
          </span>
          <span className="font-medium">
            {run?.presetLabel} → {run?.environment} ({run?.scopeLabel})
          </span>
          <span className="text-sm opacity-70">started by {run?.triggeredBy}</span>

          {/* Answers "is this still going, and when did it end?" without reading the
              log. Someone who closes the tab and comes back half an hour later could
              not previously tell a finished run from a stalled one. */}
          {run && (
            <span className="text-sm tabular" data-testid="run-timing" style={{ color: 'hsl(var(--text-muted))' }}>
              {active ? (
                <>
                  running for {formatDuration(now - new Date(run.startedAt ?? run.queuedAt).getTime())}
                  {' · started '}
                  {new Date(run.startedAt ?? run.queuedAt).toLocaleTimeString()}
                </>
              ) : run.finishedAt ? (
                <>
                  took {formatDuration(new Date(run.finishedAt).getTime() - new Date(run.startedAt ?? run.queuedAt).getTime())}
                  {' · finished '}
                  {new Date(run.finishedAt).toLocaleTimeString()}
                  {isOlderThanADay(run.finishedAt) && ` (${new Date(run.finishedAt).toLocaleDateString()})`}
                </>
              ) : null}
            </span>
          )}

          {run?.counts && run.counts.total > 0 && (
            <span className="text-sm" data-testid="run-counts">
              {run.counts.passed} passed · {run.counts.failed} failed
              {run.counts.skipped > 0 && ` · ${run.counts.skipped} skipped`}
              {/* A retry-only pass is called unstable, never green (task 11.8). */}
              {run.counts.unstable > 0 && (
                <strong className="ml-1 text-warn">· {run.counts.unstable} unstable</strong>
              )}
            </span>
          )}

          <div className="ml-auto flex gap-2">
            {active && (
              <button className="btn btn-danger" onClick={stop} disabled={stopping} data-testid="stop-run">
                {stopping ? 'Stopping…' : 'Stop run'}
              </button>
            )}
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        {run?.queueReason && (
          <p className="mt-2 text-sm text-warn" data-testid="queue-reason">
            {run.queueReason}
          </p>
        )}
        {run?.stoppedBy && (
          <p className="mt-2 text-sm opacity-70">Stopped by {run.stoppedBy}.</p>
        )}
      </div>

      {/* Layout follows the run: with Watch on, the log and the browser sit side by
          side and results go underneath. With Watch off there is no browser to
          show, so the log and results share the width instead of leaving a panel
          explaining its own absence. */}
      <div className={`grid gap-4 ${run?.watch ? 'lg:grid-cols-2' : 'lg:grid-cols-2'}`}>
        <div className="surface flex flex-col p-0">
          <div className="flex items-center justify-between px-4 py-2"
               style={{ borderBottom: '1px solid hsl(var(--border))' }}>
            <span className="text-sm font-medium">Log</span>
            {!following && (
              <button className="text-xs underline" onClick={() => setFollowing(true)}
                      data-testid="resume-follow" style={{ color: 'hsl(var(--accent))' }}>
                Jump to latest
              </button>
            )}
          </div>
          <pre
            ref={logRef}
            onScroll={onScroll}
            data-testid="run-log"
            className="log h-[28rem] overflow-auto whitespace-pre-wrap break-words p-4"
          >
            {lines.map((line) => (
              <div key={line.seq} style={{ color: lineColour(line) }}>
                {line.text}
              </div>
            ))}
            {lines.length === 0 && (
              <span style={{ color: 'hsl(var(--text-faint))' }}>Waiting for output…</span>
            )}
          </pre>
        </div>

        {/* Only rendered when the operator asked to watch. */}
        {run?.watch ? (
          <div className="surface flex flex-col p-0">
            <div className="flex items-center gap-2 px-4 py-2"
                 style={{ borderBottom: '1px solid hsl(var(--border))' }}>
              <span className="text-sm font-medium">Live browser</span>
              {run.vncUrl && <span className="dot dot-live" />}
            </div>
            {run.vncUrl && active && vncReady ? (
              /* `key` forces a remount whenever the URL changes — including the
                 relaxed retry, which usually reuses the same port number. Without
                 it the browser keeps a frame that already failed against the dead
                 container and the panel never recovers. */
              <div className="relative h-[28rem] w-full">
                <iframe
                  key={run.vncUrl}
                  ref={frameRef}
                  title="Live browser"
                  src={run.vncUrl}
                  data-testid="vnc-frame"
                  className="h-full w-full border-0"
                />
                {/* Held over the gap between tests, so the panel never goes black. */}
                {betweenTests && heldFrame && (
                  <div className="absolute inset-0" data-testid="vnc-held">
                    <img src={heldFrame} alt="" className="h-full w-full object-contain opacity-60" />
                    <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 py-2 text-xs"
                         style={{ background: 'hsl(var(--bg) / 0.85)', color: 'hsl(var(--text-muted))' }}>
                      <span className="dot dot-live" />
                      Between tests — this is the last thing the browser showed
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex h-[28rem] flex-col items-center justify-center gap-2 p-6 text-center text-sm"
                   style={{ color: 'hsl(var(--text-faint))' }}>
                {/* The runner is destroyed when the run ends, taking its VNC server
                    with it. Saying so beats leaving a dead iframe showing the
                    browser's own "refused to connect" page, which reads as a broken
                    console rather than a finished run. */}
                {run.vncUrl && active
                  ? <>
                      <span className="dot dot-live" />
                      <span data-testid="vnc-waiting">Waiting for the browser to come up…</span>
                    </>
                  : run.vncUrl
                    ? <>
                        <span data-testid="vnc-closed">The live view closed when the run finished.</span>
                        <span>The browser only exists while the run does.</span>
                      </>
                    : <span>Starting the browser…</span>}
              </div>
            )}
          </div>
        ) : (
          <LiveResults lines={lines} finished={!active} runId={runId} />
        )}
      </div>

      {/* With Watch on, results get their own full-width row below. */}
      {run?.watch && <LiveResults lines={lines} finished={!active} runId={runId} />}

      {/* What the run actually saw, under its results. */}
      <RunReports runId={runId} finished={!active} />
      <Filmstrip runId={runId} finished={!active} />

      {/* Per-test outcomes with reachable artifacts (task 13.17). At the very
          bottom: Results above already shows the same pass/fail as it streams, so
          this belongs after everything else, not competing with it up top. Failures
          first — a list of 200 passes with three failures buried in it is not a
          report. Rows expand the same way Results' do, reusing the identical
          TestDiagnostics panel (network log, steps, failure detail) rather than a
          second copy of it. */}
      {outcomes.length > 0 && (
        <div className="surface p-0" data-testid="run-outcomes">
          <div className="border-b px-4 py-2 text-sm font-medium">
            Tests ({outcomes.length})
          </div>
          <ul className="max-h-96 divide-y overflow-auto text-sm">
            {[...outcomes]
              .sort((a, b) => rank(a) - rank(b))
              .map((o, i) => {
                const key = `${o.file}:${o.title}:${i}`;
                const isOpen = expandedOutcome === key;
                const matchedShots = shotsFor(o);
                return (
                  <li key={key}>
                    <div
                      className="flex cursor-pointer flex-wrap items-baseline gap-2 px-4 py-2 hover:opacity-80"
                      onClick={() => setExpandedOutcome(isOpen ? null : key)}
                      data-testid={`outcome-row-${i}`}
                    >
                      <span
                        className={`pill ${
                          o.unstable ? 'pill-warn'
                          : o.status === 'passed' ? 'pill-pass'
                          : o.status === 'skipped' ? 'pill-info'
                          : 'pill-fail'
                        }`}
                      >
                        {o.unstable ? 'unstable' : o.status}
                      </span>

                      <span className={o.newlyFailing ? 'font-semibold' : undefined}>{o.title}</span>

                      {/* "Newly failing" is the question people actually have: a list of
                          30 failures where 29 are a fortnight old buries the one that matters. */}
                      {o.newlyFailing && (
                        <span className="pill pill-fail" data-testid="newly-failing">newly failing</span>
                      )}

                      <span className="opacity-60">{(o.durationMs / 1000).toFixed(1)}s</span>

                      {matchedShots.length > 0 && (
                        <span className="ml-auto flex gap-3 text-xs">
                          {matchedShots.map((s, n) => (
                            <a
                              key={s.index}
                              href={api.screenshotUrl(runId, s.index)}
                              target="_blank"
                              rel="noreferrer"
                              className="underline"
                              data-testid="screenshot-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              screenshot{matchedShots.length > 1 ? ` ${n + 1}` : ''}
                            </a>
                          ))}
                        </span>
                      )}

                      <span className="shrink-0 text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
                        {isOpen ? '▾' : '▸'}
                      </span>

                      {o.error && (
                        <p className="w-full text-xs text-fail">{o.error.split('\n')[0].slice(0, 220)}</p>
                      )}
                    </div>

                    {isOpen && (
                      <TestDiagnostics
                        runId={runId}
                        testTitle={o.title}
                        test={{
                          title: o.title,
                          suite: o.suite ?? '',
                          file: o.file,
                          app: appOf(o.file),
                          status: o.status,
                          unstable: o.unstable,
                        }}
                        runFinished={!active}
                      />
                    )}
                  </li>
                );
              })}
          </ul>
        </div>
      )}
    </div>
  );
}


/** Newly-failing first, then failures, then unstable, then the rest. */
function rank(o: { status: string; unstable?: boolean; newlyFailing?: boolean }): number {
  if (o.newlyFailing) return 0;
  if (o.status === 'failed' || o.status === 'timedOut') return 1;
  if (o.unstable) return 2;
  if (o.status === 'skipped') return 3;
  return 4;
}
